import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { defaultReceiptLocation } from '../../utils/locations';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import './GoodsReceiptManager.css';

// initialTab/highlightReceiptId: opened from a dashboard quality issue (that receipt is in the history tab)
const GoodsReceiptManager = ({ initialTab, highlightReceiptId }) => {
  const { t } = useTranslation('supervisor');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useMessages();
  const [activeTab, setActiveTab] = useState(initialTab || 'pending');
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [receiptHistory, setReceiptHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);

  // Receipt form state
  const [receiptForm, setReceiptForm] = useState({
    received_quantity: 0,
    rejected_quantity: 0,
    held_quantity: 0,
    destination_location: '',
    batch_number: '',
    quality_notes: '',
    create_inventory_item: true
  });

  const [locations, setLocations] = useState([]);

  // Fetch receipt history
  const fetchReceiptHistory = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/production/goods-receipt-history'));
      if (response.ok) {
        const data = await response.json();
        setReceiptHistory(data.receipts || []);
      } else {
        throw new Error('Failed to fetch receipt history');
      }
    } catch (error) {
      console.error('Error fetching receipt history:', error);
      showError(t('goodsReceipt.messages.receiptError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouseLocations();
    if (activeTab === 'pending') {
      fetchPendingReceipts();
    } else if (activeTab === 'history') {
      fetchReceiptHistory();
    }
  }, [activeTab]);

  // Bring the receipt opened from the dashboard into view once the history is loaded
  useEffect(() => {
    if (activeTab === 'history' && highlightReceiptId && receiptHistory.length > 0) {
      document.querySelector(`[data-receipt-id="${highlightReceiptId}"]`)?.scrollIntoView({ block: 'center' });
    }
  }, [activeTab, highlightReceiptId, receiptHistory]);

  const fetchWarehouseLocations = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=100'));
      if (response.ok) {
        const data = await response.json();
        // Filter to only storage locations suitable for finished goods (BULK, PALLET, SHELF, STAGING)
        const filteredLocations = data.filter(loc =>
          ['BULK', 'PALLET', 'SHELF', 'STAGING'].includes(loc.location_type)
        );
        setLocations(filteredLocations);
      } else {
        console.error('Failed to fetch warehouse locations');
        // Fallback to default location if API fails
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      // Fallback to default location
      setLocations([]);
    }
  };

  const fetchPendingReceipts = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/production/pending-receipts'));
      if (response.ok) {
        const data = await response.json();
        setPendingReceipts(data.pending_receipts || []);
      } else {
        showError(t('goodsReceipt.messages.failedToLoad'), t('goodsReceipt.messages.couldNotFetchReceipts'));
      }
    } catch (error) {
      console.error('Error fetching pending receipts:', error);
      showError(t('goodsReceipt.messages.connectionError'), t('goodsReceipt.messages.unableToConnect'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await fetchPendingReceipts();
    showInfo(t('goodsReceipt.messages.refreshed'), t('goodsReceipt.messages.pendingReceiptsUpdated'));
  };

  const handleReceiveGoods = (receipt) => {
    setSelectedReceipt(receipt);
    const defaultLocation = defaultReceiptLocation(locations);
    setReceiptForm({
      received_quantity: parseFloat(receipt.pending_receipt) || 0,
      rejected_quantity: 0,
      held_quantity: 0,
      destination_location: defaultLocation,
      batch_number: `BATCH-${localToday()}`,
      quality_notes: '',
      create_inventory_item: true,
      adjust_sales_order: false
    });
    setShowReceiptDialog(true);
  };

  const handleAcceptShortage = (receipt) => {
    setSelectedReceipt(receipt);
    const defaultLocation = defaultReceiptLocation(locations);
    setReceiptForm({
      received_quantity: 0,
      rejected_quantity: parseFloat(receipt.pending_receipt) || 0,
      held_quantity: 0,
      destination_location: defaultLocation,
      batch_number: `BATCH-${localToday()}`,
      quality_notes: t('goodsReceipt.shortage.defaultNote'),
      create_inventory_item: true,
      adjust_sales_order: true
    });
    setShowReceiptDialog(true);
  };

  const handleReceiptFormChange = (field, value) => {
    setReceiptForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateQuantities = () => {
    const received = parseFloat(receiptForm.received_quantity) || 0;
    const rejected = parseFloat(receiptForm.rejected_quantity) || 0;
    const held = parseFloat(receiptForm.held_quantity) || 0;
    const total = received + rejected + held;
    const pending = parseFloat(selectedReceipt.pending_receipt);

    if (total > pending) {
      showError(t('goodsReceipt.messages.invalidQuantity'), t('goodsReceipt.messages.totalCannotExceedPending', { total, pending }));
      return false;
    }

    if (total === 0) {
      showWarning(t('goodsReceipt.messages.noQuantitiesSpecified'), t('goodsReceipt.messages.pleaseSpecifyQuantity'));
      return false;
    }

    return true;
  };

  const handleSubmitReceipt = async () => {
    if (!validateQuantities()) {
      return;
    }

    try {
      const payload = {
        production_order_line_id: selectedReceipt.production_order_line_id,
        received_quantity: parseFloat(receiptForm.received_quantity) || 0,
        rejected_quantity: parseFloat(receiptForm.rejected_quantity) || 0,
        held_quantity: parseFloat(receiptForm.held_quantity) || 0,
        destination_location: receiptForm.destination_location,
        batch_number: receiptForm.batch_number || null,
        quality_notes: receiptForm.quality_notes || null,
        received_by: user.user_id,
        create_inventory_item: receiptForm.create_inventory_item,
        adjust_sales_order: receiptForm.adjust_sales_order || false
      };

      const response = await apiFetch(getApiUrl('/api/production/receive-goods'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(
          t('goodsReceipt.messages.goodsReceivedSuccessfully'),
          t('goodsReceipt.messages.receiptDetails', {
            receiptId: result.receipt_id,
            stockLevel: result.new_stock_level
          })
        );
        if (result.so_adjusted) {
          showSuccess(t('goodsReceipt.messages.salesOrderAdjusted'));
        }
        if (result.invoice_warning) {
          showWarning(t('goodsReceipt.messages.invoiceAdjustmentRequired'), result.invoice_warning);
        }
        setShowReceiptDialog(false);
        setSelectedReceipt(null);
        fetchPendingReceipts();
      } else {
        const error = await response.json();
        showError(t('goodsReceipt.messages.failedToReceiveGoods'), error.message || error.detail || t('goodsReceipt.messages.failedToReceiveGoods'));
      }
    } catch (error) {
      console.error('Error submitting receipt:', error);
      showError(t('goodsReceipt.messages.error'), error.message || t('goodsReceipt.messages.unexpectedError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatAppDate(dateString, 'datetime') || 'N/A';
  };

  const getUrgencyClass = (daysWaiting) => {
    if (daysWaiting > 2) return 'urgent';
    if (daysWaiting > 1) return 'warning';
    return '';
  };

  const formatQuantity = (qty) => {
    if (!qty) return '0';
    return parseFloat(qty).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'status-success';
      case 'IN_PROGRESS': return 'status-info';
      case 'PENDING': return 'status-warning';
      case 'ON_HOLD': return 'status-inactive';
      default: return 'status-secondary';
    }
  };

  const renderPendingReceipts = () => (
    <>
      {loading ? (
        <div className="loading-message">{t('goodsReceipt.messages.loadingPendingReceipts')}</div>
      ) : pendingReceipts.length === 0 ? (
        <div className="empty-message">
          ✅ {t('goodsReceipt.messages.noPendingReceipts')}
        </div>
      ) : (
        <div className="receipts-table-container">
          <table className="receipts-table">
            <thead>
              <tr>
                <th>{t('goodsReceipt.headers.poNumber')}</th>
                <th>{t('goodsReceipt.headers.line')}</th>
                <th>{t('goodsReceipt.headers.product')}</th>
                <th>{t('goodsReceipt.headers.ordered')}</th>
                <th>{t('goodsReceipt.headers.produced')}</th>
                <th>{t('goodsReceipt.headers.alreadyReceived')}</th>
                <th>{t('goodsReceipt.headers.pendingReceipt')}</th>
                <th>{t('goodsReceipt.headers.completedDate')}</th>
                <th>{t('goodsReceipt.headers.daysWaiting')}</th>
                <th>{t('goodsReceipt.headers.status')}</th>
                <th>{t('goodsReceipt.headers.action')}</th>
              </tr>
            </thead>
            <tbody>
              {pendingReceipts.map((receipt) => (
                <tr key={receipt.production_order_line_id} className={getUrgencyClass(receipt.days_waiting)}>
                  <td>{receipt.po_number}</td>
                  <td>{receipt.line_number}</td>
                  <td>
                    <div className="product-info">
                      <div className="product-code">{receipt.product_code}</div>
                      <div className="product-name">{receipt.product_name}</div>
                    </div>
                  </td>
                  <td>{receipt.order_quantity}</td>
                  <td>{receipt.produced_quantity}</td>
                  <td>{receipt.received_quantity}</td>
                  <td className="pending-qty">{receipt.pending_receipt}</td>
                  <td>{formatDate(receipt.completed_date)}</td>
                  <td className="days-waiting">
                    {receipt.days_waiting} {receipt.days_waiting === 1 ? t('goodsReceipt.labels.day') : t('goodsReceipt.labels.days')}
                  </td>
                  <td>
                    <span className={`status-badge status-${receipt.status.toLowerCase()}`}>
                      {receipt.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        className="btn-receive"
                        onClick={() => handleReceiveGoods(receipt)}
                      >
                        {t('goodsReceipt.buttons.receive')}
                      </button>
                      <button
                        className="btn-shortage"
                        onClick={() => handleAcceptShortage(receipt)}
                      >
                        {t('goodsReceipt.buttons.acceptShortage')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const renderReceiptHistory = () => (
    <>
      {loading ? (
        <div className="loading-message">{t('goodsReceipt.messages.loadingReceiptHistory')}</div>
      ) : receiptHistory.length === 0 ? (
        <div className="empty-message">
          📝 {t('goodsReceipt.messages.noReceiptHistory')}
        </div>
      ) : (
        <>
          <div className="receipts-table-container">
            <table className="receipts-table">
              <thead>
                <tr>
                  <th>{t('goodsReceipt.headers.receiptDate')}</th>
                  <th>{t('goodsReceipt.headers.poNumber')}</th>
                  <th>{t('goodsReceipt.headers.product')}</th>
                  <th>{t('goodsReceipt.headers.customer')}</th>
                  <th>{t('goodsReceipt.headers.batchNumber')}</th>
                  <th>{t('goodsReceipt.headers.received')}</th>
                  <th>{t('goodsReceipt.headers.rejected')}</th>
                  <th>{t('goodsReceipt.headers.held')}</th>
                  <th>{t('goodsReceipt.headers.location')}</th>
                  <th>{t('goodsReceipt.headers.allocated')}</th>
                  <th>{t('goodsReceipt.headers.pending')}</th>
                  <th>{t('goodsReceipt.headers.receivedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {receiptHistory.map((receipt) => (
                  <tr
                    key={receipt.receipt_id}
                    data-receipt-id={receipt.receipt_id}
                    style={String(receipt.receipt_id) === String(highlightReceiptId) ? { outline: '2px solid var(--warning, #fd7e14)', outlineOffset: '-2px' } : undefined}
                  >
                    <td>{formatDate(receipt.receipt_date)}</td>
                    <td className="po-number">{receipt.po_number}</td>
                    <td>
                      <div className="product-info">
                        <div className="product-code">{receipt.product_code}</div>
                        <div className="product-name">{receipt.product_name}</div>
                      </div>
                    </td>
                    <td className="customer-name">{receipt.customer_name}</td>
                    <td className="batch-number">{receipt.batch_number || 'N/A'}</td>
                    <td className="quantity received-qty">{formatQuantity(receipt.received_quantity)}</td>
                    <td className="quantity rejected-qty">{formatQuantity(receipt.rejected_quantity)}</td>
                    <td className="quantity held-qty">{formatQuantity(receipt.held_quantity)}</td>
                    <td className="location">{receipt.destination_location}</td>
                    <td className="quantity allocated-qty">
                      <strong>{formatQuantity(receipt.allocated_quantity)}</strong>
                    </td>
                    <td className="quantity pending-allocation">
                      {receipt.pending_allocation > 0 ? (
                        <span className="pending-highlight">{formatQuantity(receipt.pending_allocation)}</span>
                      ) : (
                        <span className="allocated-complete">✅</span>
                      )}
                    </td>
                    <td className="received-by">{receipt.received_by_name || receipt.received_by_username || t('common:ui.unknown')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="receipt-summary">
            <div className="summary-item">
              <span className="summary-label">{t('goodsReceipt.summary.totalReceipts')}:</span>
              <span className="summary-value">{receiptHistory.length}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('goodsReceipt.summary.totalReceivedQuantity')}:</span>
              <span className="summary-value">
                {formatQuantity(
                  receiptHistory.reduce((sum, item) => sum + parseFloat(item.received_quantity || 0), 0)
                )}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('goodsReceipt.summary.allocatedVsPending')}:</span>
              <span className="summary-value">
                {formatQuantity(
                  receiptHistory.reduce((sum, item) => sum + parseFloat(item.allocated_quantity || 0), 0)
                )} {t('goodsReceipt.summary.allocated')} / {formatQuantity(
                  receiptHistory.reduce((sum, item) => sum + parseFloat(item.pending_allocation || 0), 0)
                )} {t('goodsReceipt.summary.pending')}
              </span>
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="goods-receipt-manager">
      <div className="grm-header">
        <h2>📥 {t('goodsReceipt.title')}</h2>
        <button
          className="btn-refresh"
          onClick={activeTab === 'pending' ? handleRefresh : fetchReceiptHistory}
          disabled={loading}
        >
          🔄 {t('goodsReceipt.buttons.refresh')}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="receipt-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          📥 {t('goodsReceipt.sections.pendingReceipts')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📋 {t('goodsReceipt.sections.receiptHistory')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'pending' ? renderPendingReceipts() : renderReceiptHistory()}
      </div>

      {/* Receipt Dialog */}
      {showReceiptDialog && selectedReceipt && (
        <div className="modal-overlay" onClick={() => setShowReceiptDialog(false)}>
          <div className="receipt-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>{t('goodsReceipt.dialog.title')} - {selectedReceipt.po_number} {t('goodsReceipt.dialog.line')} {selectedReceipt.line_number}</h3>
              <button className="btn-close" onClick={() => setShowReceiptDialog(false)}>✕</button>
            </div>

            <div className="dialog-content">
              <div className="receipt-summary">
                <div className="summary-item">
                  <label>{t('goodsReceipt.dialog.product')}:</label>
                  <span>{selectedReceipt.product_name} ({selectedReceipt.product_code})</span>
                </div>
                <div className="summary-item">
                  <label>{t('goodsReceipt.dialog.ordered')}:</label>
                  <span>{selectedReceipt.order_quantity} {t('goodsReceipt.labels.units')}</span>
                </div>
                <div className="summary-item">
                  <label>{t('goodsReceipt.dialog.produced')}:</label>
                  <span>{selectedReceipt.produced_quantity} {t('goodsReceipt.labels.units')} ({t('goodsReceipt.dialog.reportedByOperator')})</span>
                </div>
                <div className="summary-item">
                  <label>{t('goodsReceipt.dialog.alreadyReceived')}:</label>
                  <span>{selectedReceipt.received_quantity} {t('goodsReceipt.labels.units')}</span>
                </div>
                <div className="summary-item highlight">
                  <label>{t('goodsReceipt.dialog.pending')}:</label>
                  <span>{selectedReceipt.pending_receipt} {t('goodsReceipt.labels.units')}</span>
                </div>
              </div>

              <div className="receipt-form">
                <div className="form-group">
                  <label>{t('goodsReceipt.form.receiveQuantity')}: *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={receiptForm.received_quantity}
                    onChange={(e) => handleReceiptFormChange('received_quantity', e.target.value)}
                  />
                  <span className="field-help">{t('goodsReceipt.form.receiveQuantityHelp')}</span>
                </div>

                <div className="form-group">
                  <label>{t('goodsReceipt.form.rejectQuantity')}:</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={receiptForm.rejected_quantity}
                    onChange={(e) => handleReceiptFormChange('rejected_quantity', e.target.value)}
                  />
                  <span className="field-help">{t('goodsReceipt.form.rejectQuantityHelp')}</span>
                </div>

                <div className="form-group">
                  <label>{t('goodsReceipt.form.holdQuantity')}:</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={receiptForm.held_quantity}
                    onChange={(e) => handleReceiptFormChange('held_quantity', e.target.value)}
                  />
                  <span className="field-help">{t('goodsReceipt.form.holdQuantityHelp')}</span>
                </div>

                <div className="form-group">
                  <label>{t('goodsReceipt.form.destinationLocation')}: *</label>
                  <select
                    value={receiptForm.destination_location}
                    onChange={(e) => handleReceiptFormChange('destination_location', e.target.value)}
                  >
                    {locations.map(loc => (
                      <option key={loc.location_code} value={loc.location_code}>
                        {loc.location_code} - {loc.location_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('goodsReceipt.form.batchNumber')}:</label>
                  <input
                    type="text"
                    value={receiptForm.batch_number}
                    onChange={(e) => handleReceiptFormChange('batch_number', e.target.value)}
                    placeholder={t('goodsReceipt.form.batchNumberPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('goodsReceipt.form.qualityNotes')}:</label>
                  <textarea
                    rows="3"
                    value={receiptForm.quality_notes}
                    onChange={(e) => handleReceiptFormChange('quality_notes', e.target.value)}
                    placeholder={t('goodsReceipt.form.qualityNotesPlaceholder')}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={receiptForm.create_inventory_item}
                      onChange={(e) => handleReceiptFormChange('create_inventory_item', e.target.checked)}
                    />
                    <span>{t('goodsReceipt.form.createInventoryItem')}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-cancel" onClick={() => setShowReceiptDialog(false)}>
                {t('goodsReceipt.buttons.cancel')}
              </button>
              <button className="btn-submit" onClick={handleSubmitReceipt}>
                {t('goodsReceipt.buttons.receiveToInventory')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsReceiptManager;
